import type {
    AudioMuteHandler,
    HostGameStatus,
    HostIntegration,
    HostRoom,
    PlayerAuthenticationHandler,
    PlayerAuthenticationResult,
    PlayerCredentialResult,
    RoomJoinHandler,
    RoomLaunchIntent,
} from "./types";

type CrazyGamesSession = {
    authenticated: boolean;
    returnHome: boolean;
    instantLaunchHandled: boolean;
    dismissedRoomId: string | null;
};

const SESSION_KEY = "LASTDOTCARDS.CRAZYGAMES.SESSION";
const EMPTY_SESSION: CrazyGamesSession = {
    authenticated: false,
    returnHome: false,
    instantLaunchHandled: false,
    dismissedRoomId: null,
};

const readSession = (): CrazyGamesSession => {
    try {
        const rawSession = window.sessionStorage.getItem(SESSION_KEY);

        if(!rawSession) {
            return { ...EMPTY_SESSION };
        }

        const session = JSON.parse(rawSession) as Partial<CrazyGamesSession>;

        return {
            authenticated: session.authenticated === true,
            returnHome: session.returnHome === true,
            instantLaunchHandled: session.instantLaunchHandled === true,
            dismissedRoomId: typeof session.dismissedRoomId === "string"
                ? session.dismissedRoomId
                : null,
        };
    } catch(_) {
        return { ...EMPTY_SESSION };
    }
};

class CrazyGamesHostIntegration implements HostIntegration {
    private initialized = false;
    private initialization: Promise<void> | null = null;
    private pendingGameStatus: HostGameStatus = {
        ready: false,
        roomIsPlaying: false,
    };
    private gameStatus: HostGameStatus = {
        ready: false,
        roomIsPlaying: false,
    };
    private pendingRoom: HostRoom | null | undefined;
    private roomJoinHandlers = new Set<RoomJoinHandler>();
    private roomJoinListener: ((params: CrazyGamesInviteParams) => void) | null = null;
    private audioMuteHandlers = new Set<AudioMuteHandler>();
    private audioMuteListener: ((settings: CrazyGamesGameSettings) => void) | null = null;
    private playerAuthenticationHandlers = new Set<PlayerAuthenticationHandler>();
    private playerAuthenticationListener: ((user: unknown) => void) | null = null;
    private roomInviteUrls = new Map<string, string>();
    private session = readSession();

    initialize(): Promise<void> {
        if(this.initialized) {
            return Promise.resolve();
        }

        if(!this.initialization) {
            this.initialization = this.initializeSdk();
        }

        return this.initialization;
    }

    async getPlayerCredential(): Promise<PlayerCredentialResult> {
        await this.initialize();

        const user = this.initialized
            ? window.CrazyGames?.SDK.user
            : undefined;

        if(!user?.isUserAccountAvailable) {
            return {
                status: "unavailable",
                credential: null,
            };
        }

        try {
            const credential = await user.getUserToken();

            this.updateSession({
                authenticated: true,
                returnHome: false,
            });

            return {
                status: "authenticated",
                credential,
            };
        } catch(error) {
            const code = this.getSdkErrorCode(error);

            if(code === "userNotAuthenticated") {
                this.updateSession({
                    authenticated: false,
                    returnHome: this.session.returnHome
                        || this.session.authenticated,
                });

                return {
                    status: "not-authenticated",
                    credential: null,
                };
            }

            // eslint-disable-next-line no-console
            console.error("[CrazyGames] Failed to get player credential", error);

            return {
                status: "unavailable",
                credential: null,
            };
        }
    }

    async requestPlayerAuthentication(): Promise<PlayerAuthenticationResult> {
        await this.initialize();

        const user = this.initialized
            ? window.CrazyGames?.SDK.user
            : undefined;

        if(!user?.isUserAccountAvailable) {
            return "not-authenticated";
        }

        try {
            await user.showAuthPrompt();

            return "authenticated";
        } catch(error) {
            const code = this.getSdkErrorCode(error);

            if(code === "userAlreadySignedIn") {
                return "already-authenticated";
            }

            if(!["showAuthPromptInProgress", "userCancelled"].includes(code)) {
                // eslint-disable-next-line no-console
                console.error("[CrazyGames] Failed to authenticate player", error);
            }

            return "not-authenticated";
        }
    }

    onPlayerAuthenticated(handler: PlayerAuthenticationHandler): () => void {
        this.playerAuthenticationHandlers.add(handler);
        this.attachPlayerAuthenticationListener();

        return () => {
            this.playerAuthenticationHandlers.delete(handler);
            this.detachPlayerAuthenticationListenerIfUnused();
        };
    }

    syncGameStatus(nextStatus: HostGameStatus): void {
        this.pendingGameStatus = nextStatus;

        if(!this.initialized) {
            return;
        }

        const game = window.CrazyGames?.SDK.game;

        if(!game) {
            return;
        }

        if(this.gameStatus.roomIsPlaying && !nextStatus.roomIsPlaying) {
            game.gameplayStop();
        }

        if(this.gameStatus.ready && !nextStatus.ready) {
            game.loadingStart();
        }

        if(!this.gameStatus.ready && nextStatus.ready) {
            game.loadingStop();
        }

        if(!this.gameStatus.roomIsPlaying && nextStatus.roomIsPlaying) {
            game.gameplayStart();
        }

        this.gameStatus = nextStatus;
    }

    syncRoom(room: HostRoom | null): void {
        const previousRoom = this.pendingRoom;

        this.pendingRoom = room;

        if(!this.initialized) {
            return;
        }

        const game = window.CrazyGames?.SDK.game;

        if(!game) {
            return;
        }

        if(!room) {
            if(previousRoom) {
                this.updateSession({ dismissedRoomId: previousRoom.id });
            }

            game.leftRoom();
            return;
        }

        game.updateRoom({
            roomId: room.id,
            isJoinable: room.joinable,
            inviteParams: { roomId: room.id },
        });
    }

    consumeRoomLaunchIntent(): RoomLaunchIntent {
        if(this.session.returnHome) {
            this.updateSession({
                returnHome: false,
                instantLaunchHandled: false,
            });

            return { type: "show-home" };
        }

        const game = this.getGameModule();
        const roomId = game?.inviteParams?.roomId;

        if(roomId && roomId === this.session.dismissedRoomId) {
            return { type: "show-home" };
        }

        if(roomId) {
            return { type: "join", roomId };
        }

        if(!game?.isInstantMultiplayer) {
            this.updateSession({ instantLaunchHandled: false });

            return { type: "show-home" };
        }

        if(this.session.instantLaunchHandled) {
            return { type: "show-home" };
        }

        this.updateSession({ instantLaunchHandled: true });

        return { type: "create-private" };
    }

    onRoomJoinRequested(handler: RoomJoinHandler): () => void {
        this.roomJoinHandlers.add(handler);
        this.attachRoomJoinListener();

        return () => {
            this.roomJoinHandlers.delete(handler);
            this.detachRoomJoinListenerIfUnused();
        };
    }

    onAudioMuteChanged(handler: AudioMuteHandler): () => void {
        this.audioMuteHandlers.add(handler);
        this.attachAudioMuteListener();

        const game = this.getGameModule();

        if(game) {
            handler(Boolean(game.settings.muteAudio));
        }

        return () => {
            this.audioMuteHandlers.delete(handler);
            this.detachAudioMuteListenerIfUnused();
        };
    }

    createRoomInviteUrl(roomId: string): string | null {
        const cachedUrl = this.roomInviteUrls.get(roomId);

        if(cachedUrl) {
            return cachedUrl;
        }

        if(!this.initialized) {
            return null;
        }

        const inviteUrl = window.CrazyGames?.SDK.game.inviteLink({ roomId }) ?? null;

        if(inviteUrl) {
            this.roomInviteUrls.set(roomId, inviteUrl);
        }

        return inviteUrl;
    }

    private getGameModule(): CrazyGamesGameModule | undefined {
        return this.initialized
            ? window.CrazyGames?.SDK.game
            : undefined;
    }

    private getSdkErrorCode(error: unknown): string {
        return typeof error === "object" && error !== null && "code" in error
            ? String(error.code)
            : "";
    }

    private async initializeSdk(): Promise<void> {
        try {
            const sdkReady = window.__crazyGamesSDKReady;

            if(sdkReady) {
                await sdkReady;
            }

            const sdk = window.CrazyGames?.SDK;

            if(!sdk) {
                throw new Error("CrazyGames SDK v3 was not loaded");
            }

            if(!sdkReady) {
                await sdk.init();
            }

            if(sdk.environment === "disabled") {
                return;
            }

            if(!sdkReady) {
                sdk.game.loadingStart();
            }

            this.initialized = true;
            this.syncGameStatus(this.pendingGameStatus);

            if(this.pendingRoom !== undefined) {
                this.syncRoom(this.pendingRoom);
            }

            this.attachRoomJoinListener();
            this.attachAudioMuteListener();
            this.attachPlayerAuthenticationListener();
            this.notifyAudioMute(Boolean(sdk.game.settings.muteAudio));
        } catch(error) {
            // A portal outage must not make the game itself unusable.
            // eslint-disable-next-line no-console
            console.error("[CrazyGames] SDK initialization failed", error);
        } finally {
            this.initialization = null;
        }
    }

    private attachRoomJoinListener(): void {
        const game = this.getGameModule();

        if(!game || this.roomJoinListener || this.roomJoinHandlers.size === 0) {
            return;
        }

        this.roomJoinListener = (params) => {
            const roomId = params.roomId;

            if(!roomId) {
                return;
            }

            this.updateSession({ dismissedRoomId: null });
            this.roomJoinHandlers.forEach((handler) => handler(roomId));
        };

        game.addJoinRoomListener(this.roomJoinListener);
    }

    private detachRoomJoinListenerIfUnused(): void {
        const game = this.getGameModule();

        if(!game || !this.roomJoinListener || this.roomJoinHandlers.size > 0) {
            return;
        }

        game.removeJoinRoomListener(this.roomJoinListener);
        this.roomJoinListener = null;
    }

    private attachPlayerAuthenticationListener(): void {
        const user = this.initialized
            ? window.CrazyGames?.SDK.user
            : undefined;

        if(
            !user?.isUserAccountAvailable
            || this.playerAuthenticationListener
            || this.playerAuthenticationHandlers.size === 0
        ) {
            return;
        }

        this.playerAuthenticationListener = () => {
            this.playerAuthenticationHandlers.forEach((handler) => handler());
        };

        user.addAuthListener(this.playerAuthenticationListener);
    }

    private detachPlayerAuthenticationListenerIfUnused(): void {
        const user = this.initialized
            ? window.CrazyGames?.SDK.user
            : undefined;

        if(
            !user
            || !this.playerAuthenticationListener
            || this.playerAuthenticationHandlers.size > 0
        ) {
            return;
        }

        user.removeAuthListener(this.playerAuthenticationListener);
        this.playerAuthenticationListener = null;
    }

    private attachAudioMuteListener(): void {
        const game = this.getGameModule();

        if(!game || this.audioMuteListener || this.audioMuteHandlers.size === 0) {
            return;
        }

        this.audioMuteListener = (settings) => {
            this.notifyAudioMute(Boolean(settings.muteAudio));
        };

        game.addSettingsChangeListener(this.audioMuteListener);
    }

    private detachAudioMuteListenerIfUnused(): void {
        const game = this.getGameModule();

        if(!game || !this.audioMuteListener || this.audioMuteHandlers.size > 0) {
            return;
        }

        game.removeSettingsChangeListener(this.audioMuteListener);
        this.audioMuteListener = null;
    }

    private notifyAudioMute(muted: boolean): void {
        this.audioMuteHandlers.forEach((handler) => handler(muted));
    }

    private updateSession(patch: Partial<CrazyGamesSession>): void {
        this.session = {
            ...this.session,
            ...patch,
        };

        try {
            window.sessionStorage.setItem(
                SESSION_KEY,
                JSON.stringify(this.session),
            );
        } catch(_) {
            // The in-memory state still protects the current page lifecycle.
        }
    }
}

export default CrazyGamesHostIntegration;
